import { useEffect, useRef, useState } from "react";
import { api, apiErr, openFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Loader, EmptyState } from "@/components/ui-helpers";
import { toast } from "sonner";
import { FolderOpen, Upload, Trash2, FileText, Image as ImageIcon, Receipt } from "lucide-react";

const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

// Client Files: one place for every file on a client. Payment proofs are filed here automatically
// (linked to the payment + its receipt number); staff can upload other files by category.
export function ClientFiles({ clientId }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("all");
  const [upCat, setUpCat] = useState("Other");
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const load = () => api.get(`/clients/${clientId}/files`).then((r) => setData(r.data)).catch(() => setData({ categories: [], files: [] }));
  useEffect(() => { load(); }, [clientId]); // eslint-disable-line

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); fd.append("category", upCat); await api.post(`/clients/${clientId}/files`, fd); toast.success("File uploaded"); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const remove = async (f) => {
    if (!window.confirm(`Delete ${f.file_name}?${f.payment_id ? " It is the proof for a recorded payment." : ""}`)) return;
    try { await api.delete(`/client-files/${f.id}`); toast.success("File deleted"); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const open = async (f) => { try { await openFile(`/client-files/${f.id}/download`); } catch (e) { toast.error(apiErr(e)); } };

  if (!data) return <Loader />;
  const cats = data.categories || [];
  const counts = Object.fromEntries(cats.map((c) => [c, data.files.filter((f) => f.category === c).length]));
  const shown = data.files.filter((f) => cat === "all" || f.category === cat);
  const uploadCats = cats.filter((c) => c !== "Payment proofs");

  return (
    <Card className="p-5" data-testid="client-files">
      <div className="flex flex-wrap items-center gap-2 justify-between mb-4">
        <div className="flex flex-wrap gap-1" data-testid="client-files-filters">
          {[["all", `All (${data.files.length})`], ...cats.map((c) => [c, `${c} (${counts[c] || 0})`])].map(([k, l]) => (
            <button key={k} data-testid={`client-files-cat-${k}`} onClick={() => setCat(k)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${cat === k ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="client-files-upload-cat" value={upCat} onChange={(e) => setUpCat(e.target.value)} className="px-2.5 py-2 rounded-lg border border-slate-200 text-xs">
            {uploadCats.map((c) => <option key={c}>{c}</option>)}
          </select>
          <input ref={ref} type="file" className="hidden" data-testid="client-files-input" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
          <button data-testid="client-files-upload" disabled={busy} onClick={() => ref.current?.click()} className="inline-flex items-center gap-1.5 bg-primary text-white px-3 py-2 rounded-lg text-xs font-semibold disabled:opacity-50"><Upload className="h-3.5 w-3.5" /> {busy ? "Uploading…" : "Upload file"}</button>
        </div>
      </div>
      {shown.length === 0 ? <EmptyState icon={FolderOpen} title="No files yet" description={cat === "Payment proofs" ? "Payment screenshots are saved here automatically when a payment is recorded." : "Upload client files here."} /> : (
        <div className="divide-y divide-slate-100" data-testid="client-files-list">
          {shown.map((f) => {
            const isImg = (f.content_type || "").startsWith("image/");
            return (
              <div key={f.id} className="py-3 flex items-center gap-3" data-testid={`client-file-${f.id}`}>
                <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${f.category === "Payment proofs" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
                  {f.category === "Payment proofs" ? <Receipt className="h-4 w-4" /> : isImg ? <ImageIcon className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <button onClick={() => open(f)} data-testid={`client-file-open-${f.id}`} className="font-medium text-sm text-slate-800 hover:text-primary truncate block max-w-full text-left">{f.file_name}</button>
                  <div className="text-[11px] text-slate-400">
                    {f.category} · {fmtSize(f.size || 0)} · {fmtDate(f.created_at)} · by {f.uploaded_by}
                    {f.payment_id && <> · <span className="text-emerald-700 font-semibold" data-testid={`client-file-payment-${f.id}`}>Payment {f.amount != null ? Number(f.amount).toFixed(2) : ""}{f.receipt_number ? ` · ${f.receipt_number}` : ""}{f.payment_status && f.payment_status !== "Completed" ? ` · ${f.payment_status}` : ""}</span></>}
                    {f.category === "Payment proofs" && !f.payment_id && <> · <span className="text-amber-600">not linked to a payment</span></>}
                  </div>
                </div>
                {(isAdmin || !f.payment_id) && <button onClick={() => remove(f)} data-testid={`client-file-delete-${f.id}`} title="Delete file" className="text-slate-300 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
